/**
 * CodeBlock
 *
 * Compact code display used by AI Elements tool surfaces.
 *
 * Primary responsibilities:
 * - Display source or terminal output in a stable monospace panel
 * - Provide copy affordances for generated code
 *
 * Dependencies:
 * - useCopyToClipboard
 * - UI Button
 */

'use client'

import {
  createContext,
  type ComponentProps,
  type ReactNode,
  useContext,
  useMemo
} from 'react'
import { Check, Clipboard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCopyToClipboard } from '@/lib/hooks/use-copy-to-clipboard'
import { cn } from '@/lib/utils'

interface CodeBlockContextValue {
  code: string
  language?: string
}

const CodeBlockContext = createContext<CodeBlockContextValue | null>(null)

function useCodeBlock() {
  const context = useContext(CodeBlockContext)

  if (!context) {
    throw new Error('CodeBlock components must be used within CodeBlock')
  }

  return context
}

export interface CodeBlockProps extends ComponentProps<'div'> {
  code: string
  language?: string
  showLineNumbers?: boolean
}

export function CodeBlock({
  code,
  language,
  showLineNumbers = false,
  className,
  children,
  ...props
}: CodeBlockProps) {
  const contextValue = useMemo(
    () => ({
      code,
      language
    }),
    [code, language]
  )

  return (
    <CodeBlockContext.Provider value={contextValue}>
      <div
        className={cn('overflow-hidden rounded-md border border-border/70 bg-background', className)}
        {...props}
      >
        {children ?? (
          <>
            <CodeBlockHeader>
              <CodeBlockTitle>{language ?? 'code'}</CodeBlockTitle>
              <CodeBlockActions>
                <CodeBlockCopyButton />
              </CodeBlockActions>
            </CodeBlockHeader>
            <CodeBlockContent showLineNumbers={showLineNumbers} />
          </>
        )}
      </div>
    </CodeBlockContext.Provider>
  )
}

export function CodeBlockHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 border-b border-border/60 bg-muted/30 px-2 py-1.5',
        className
      )}
      {...props}
    />
  )
}

export function CodeBlockTitle({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('min-w-0 truncate font-mono text-[11px] text-muted-foreground', className)}
      {...props}
    />
  )
}

export function CodeBlockFilename(props: ComponentProps<typeof CodeBlockTitle>) {
  return <CodeBlockTitle {...props} />
}

export function CodeBlockActions({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex shrink-0 items-center gap-1', className)} {...props} />
}

export interface CodeBlockContentProps extends ComponentProps<'pre'> {
  code?: string
  showLineNumbers?: boolean
  children?: ReactNode
}

export function CodeBlockContent({
  code,
  showLineNumbers = false,
  className,
  children,
  ...props
}: CodeBlockContentProps) {
  const context = useCodeBlock()
  const value = code ?? context.code
  const lines = value.split('\n')

  return (
    <pre
      className={cn(
        'max-h-80 overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-xs leading-relaxed text-foreground',
        className
      )}
      {...props}
    >
      {children ??
        (showLineNumbers ? (
          lines.map((line, index) => (
            <span key={`${index}-${line}`} className="block">
              <span className="mr-3 inline-block w-6 select-none text-right text-muted-foreground">
                {index + 1}
              </span>
              {line || ' '}
            </span>
          ))
        ) : (
          value
        ))}
    </pre>
  )
}

export interface CodeBlockCopyButtonProps extends Omit<ComponentProps<typeof Button>, 'onCopy'> {
  onCopy?: () => void
  onError?: (error: Error) => void
  timeout?: number
}

export function CodeBlockCopyButton({
  onCopy,
  onError,
  timeout,
  className,
  ...props
}: CodeBlockCopyButtonProps) {
  const { code } = useCodeBlock()
  const { isCopied, copyToClipboard } = useCopyToClipboard({ timeout })

  return (
    <Button
      aria-label={isCopied ? 'Copied' : 'Copy code'}
      className={cn('size-7', className)}
      size="icon"
      type="button"
      variant="ghost"
      onClick={() => {
        try {
          copyToClipboard(code, { exact: true })
          onCopy?.()
        } catch (error) {
          onError?.(error instanceof Error ? error : new Error('Unable to copy code'))
        }
      }}
      {...props}
    >
      {isCopied ? <Check className="size-3.5" /> : <Clipboard className="size-3.5" />}
    </Button>
  )
}
